'use client'

/**
 * How the Presenter window and the stage window find each other (plans/07
 * 5.3 and 5.3a; plans/08 4.6 and 12 item 1).
 *
 * - The stage is a named browser tab, `aio-stage`, opened at `/?stage=1`. It
 *   marks itself in sessionStorage (`aio.stage` = `1`), so the mark survives
 *   in-app navigation and reloads of that tab only.
 * - The server holds the truth (`story.seq`, `story.stage`); the stage window
 *   polls it. To follow at once, the Presenter also writes a cue to
 *   localStorage after every move; the `storage` event wakes the stage tab of
 *   the same browser profile, which takes the cue's persona and page.
 * - The stage tab writes a heartbeat to localStorage, so the Presenter knows
 *   whether a stage window is open. Without one, a step opens in the
 *   Presenter's own window (single-window fallback, `aio.stage` = `single`).
 */

import { isPersonaId, type PersonaId } from '@/lib/persona'

export const STAGE_WINDOW_NAME = 'aio-stage'
/** sessionStorage: `1` in the stage window, `single` when the Presenter window doubles as the stage. */
export const STAGE_STORAGE_KEY = 'aio.stage'
/** localStorage: the stage window's heartbeat (epoch ms). */
export const STAGE_HEARTBEAT_KEY = 'aio.stage.alive'
/** localStorage: the Presenter's last cue, `{href, persona, seq, at}`. */
export const STAGE_CUE_KEY = 'aio.stage.cue'
/** sessionStorage: the operator typed RESET once in this window's session (plans/07 5.2). */
export const RESET_TYPED_KEY = 'aio.presenter.reset_typed'

/** Window event fired when this window's stage mode changes (sessionStorage has no cross-component event). */
export const STAGE_MODE_EVENT = 'aio-stage-mode'

export const STAGE_HEARTBEAT_MS = 2000
const STAGE_ALIVE_WITHIN_MS = 6000

export type StageMode = '1' | 'single'

export interface StageCue {
  href: string
  persona: PersonaId
  seq: number
  at: number
}

function session(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

function local(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export function getStageMode(): StageMode | null {
  const value = session()?.getItem(STAGE_STORAGE_KEY)
  return value === '1' || value === 'single' ? value : null
}

export function setStageMode(mode: StageMode | null): void {
  const store = session()
  if (!store) return
  try {
    if (store.getItem(STAGE_STORAGE_KEY) === mode) return
    if (mode) store.setItem(STAGE_STORAGE_KEY, mode)
    else store.removeItem(STAGE_STORAGE_KEY)
  } catch {
    // Storage blocked: this window simply does not follow.
    return
  }
  window.dispatchEvent(new Event(STAGE_MODE_EVENT))
}

/** Opens (or re-focuses and reloads) the named stage tab. */
export function openStageWindow(): Window | null {
  if (typeof window === 'undefined') return null
  const stage = window.open('/?stage=1', STAGE_WINDOW_NAME)
  stage?.focus()
  return stage
}

export function writeHeartbeat(): void {
  try {
    local()?.setItem(STAGE_HEARTBEAT_KEY, String(Date.now()))
  } catch {
    // Storage blocked: the Presenter falls back to its own window.
  }
}

/** True when a stage window wrote its heartbeat in the last few seconds. */
export function stageWindowAlive(): boolean {
  const raw = local()?.getItem(STAGE_HEARTBEAT_KEY)
  const at = raw ? Number(raw) : 0
  return Number.isFinite(at) && Date.now() - at < STAGE_ALIVE_WITHIN_MS
}

/** Tells a stage tab in this browser profile where to go now (it also follows the server on its next poll). */
export function cueStage(href: string, persona: PersonaId, seq: number): void {
  const cue: StageCue = { href, persona, seq, at: Date.now() }
  try {
    local()?.setItem(STAGE_CUE_KEY, JSON.stringify(cue))
  } catch {
    // Storage blocked: the stage follows on its next poll.
  }
}

export function parseCue(raw: string | null): StageCue | null {
  if (!raw) return null
  try {
    const cue = JSON.parse(raw) as Partial<StageCue>
    if (typeof cue.href !== 'string' || !cue.href.startsWith('/') || cue.href.startsWith('//')) return null
    if (!isPersonaId(cue.persona)) return null
    return { href: cue.href, persona: cue.persona, seq: Number(cue.seq) || 0, at: Number(cue.at) || 0 }
  } catch {
    return null
  }
}

export function resetTypedThisSession(): boolean {
  return session()?.getItem(RESET_TYPED_KEY) === '1'
}

export function rememberResetTyped(): void {
  try {
    session()?.setItem(RESET_TYPED_KEY, '1')
  } catch {
    // Storage blocked: the operator types RESET again next time.
  }
}

/** The app route a stage target names, or null when it would leave the app. */
export function safeAppHref(href: string | null | undefined): string | null {
  if (!href || !href.startsWith('/') || href.startsWith('//') || href.includes('\\')) return null
  return href
}

/** True when the key event is Shift+Alt plus `code` and focus is not in a text field. */
export function isStageShortcut(event: KeyboardEvent): boolean {
  if (!event.shiftKey || !event.altKey || event.ctrlKey || event.metaKey) return false
  const target = event.target as HTMLElement | null
  if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return false
  return true
}
